import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ALERT_TYPES = [
  { value: 'EXPOSURE_WARNING', label: 'Exposición - Advertencia', unit: '%' },
  { value: 'EXPOSURE_CRITICAL', label: 'Exposición - Crítico', unit: '%' },
  { value: 'INVENTORY_AGE', label: 'Antigüedad de Inventario', unit: 'días' },
  { value: 'MARGIN_BELOW_MIN', label: 'Margen Mínimo', unit: '%' },
  { value: 'BENCHMARK_SHOCK', label: 'Shock de Benchmark', unit: '%' },
  { value: 'CONCENTRATION_LIMIT', label: 'Límite de Concentración', unit: '%' },
];

const TYPE_LABELS = Object.fromEntries(ALERT_TYPES?.map(t => [t?.value, t?.label]));
const TYPE_UNITS = Object.fromEntries(ALERT_TYPES?.map(t => [t?.value, t?.unit]));

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso)?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

export default function AlertRulesPanel({ rules, metals, categories, loading, onToggle, onCreate, onEdit, onDelete }) {
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState({ type: 'EXPOSURE_WARNING', threshold_value: '', scope_metal_id: '', scope_category_id: '', is_enabled: true });
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const openCreate = () => {
    setEditingRule(null);
    setForm({ type: 'EXPOSURE_WARNING', threshold_value: '', scope_metal_id: '', scope_category_id: '', is_enabled: true });
    setShowModal(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      type: rule?.type || 'EXPOSURE_WARNING',
      threshold_value: rule?.threshold_value ?? '',
      scope_metal_id: rule?.scope_metal_id || '',
      scope_category_id: rule?.scope_category_id || '',
      is_enabled: rule?.is_enabled ?? true,
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form?.threshold_value && form?.threshold_value !== 0) return;
    setSubmitting(true);
    if (editingRule) {
      await onEdit?.(editingRule?.id, {
        type: form?.type,
        threshold_value: parseFloat(form?.threshold_value),
        scope_metal_id: form?.scope_metal_id || null,
        scope_category_id: form?.scope_category_id || null,
        is_enabled: form?.is_enabled,
      });
    } else {
      await onCreate?.({
        type: form?.type,
        threshold_value: parseFloat(form?.threshold_value),
        scope_metal_id: form?.scope_metal_id || null,
        scope_category_id: form?.scope_category_id || null,
        is_enabled: form?.is_enabled,
      });
    }
    setSubmitting(false);
    setShowModal(false);
  };

  const handleDelete = async (id) => {
    await onDelete?.(id);
    setDeleteConfirm(null);
  };

  const selectedUnit = TYPE_UNITS?.[form?.type] || '';

  return (
    <div className="bg-card border border-border rounded-xl" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Settings2" size={16} color="var(--color-accent)" strokeWidth={2} />
          <h2 className="text-sm font-semibold text-foreground">Reglas de Alerta</h2>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{(rules || [])?.length}</span>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
          style={{ background: 'var(--color-accent)' }}
        >
          <Icon name="Plus" size={13} color="#fff" />
          Nueva regla
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-2.5 text-left text-muted-foreground font-medium">Tipo</th>
              <th className="px-4 py-2.5 text-right text-muted-foreground font-medium">Umbral</th>
              <th className="px-4 py-2.5 text-left text-muted-foreground font-medium">Alcance</th>
              <th className="px-4 py-2.5 text-center text-muted-foreground font-medium">Activa</th>
              <th className="px-4 py-2.5 text-left text-muted-foreground font-medium">Último disparo</th>
              <th className="px-4 py-2.5 text-center text-muted-foreground font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 })?.map((_, i) => (
                <tr key={i} className="border-b border-border/50">
                  {Array.from({ length: 6 })?.map((_, j) => (
                    <td key={j} className="px-4 py-2.5"><div className="h-3.5 bg-muted rounded animate-pulse" /></td>
                  ))}
                </tr>
              ))
            ) : (rules || [])?.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="Settings2" size={24} color="var(--color-muted-foreground)" />
                    <p className="text-sm text-muted-foreground">Sin reglas configuradas</p>
                    <button onClick={openCreate} className="text-xs text-accent hover:underline">Crear primera regla</button>
                  </div>
                </td>
              </tr>
            ) : (
              (rules || [])?.map(rule => {
                const metalName = metals?.find(m => m?.id === rule?.scope_metal_id)?.name;
                const catName = categories?.find(c => c?.id === rule?.scope_category_id)?.name;
                const scopeLabel = metalName || catName || 'Global';
                return (
                  <tr key={rule?.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-foreground">{TYPE_LABELS?.[rule?.type] || rule?.type}</td>
                    <td className="px-4 py-2.5 font-mono text-right">
                      {rule?.threshold_value}{TYPE_UNITS?.[rule?.type] || ''}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                        {scopeLabel}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <button
                        onClick={() => onToggle?.(rule?.id, !rule?.is_enabled)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          rule?.is_enabled ? 'bg-emerald-500' : 'bg-muted'
                        }`}
                        aria-label={rule?.is_enabled ? 'Desactivar' : 'Activar'}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                          rule?.is_enabled ? 'translate-x-4' : 'translate-x-1'
                        }`} />
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground font-mono">{fmtDate(rule?.last_triggered_at)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEdit(rule)}
                          className="p-1.5 rounded hover:bg-muted transition-colors"
                          title="Editar"
                        >
                          <Icon name="Pencil" size={13} color="var(--color-muted-foreground)" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(rule?.id)}
                          className="p-1.5 rounded hover:bg-red-50 transition-colors"
                          title="Eliminar"
                        >
                          <Icon name="Trash2" size={13} color="#ef4444" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md" style={{ boxShadow: 'var(--shadow-xl)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-semibold text-foreground">
                {editingRule ? 'Editar regla' : 'Nueva regla de alerta'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 rounded hover:bg-muted transition-colors">
                <Icon name="X" size={16} color="var(--color-muted-foreground)" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Type */}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Tipo de alerta</label>
                <select
                  value={form?.type}
                  onChange={e => setForm(f => ({ ...f, type: e?.target?.value }))}
                  className="w-full px-3 py-2 text-xs bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  {ALERT_TYPES?.map(t => (
                    <option key={t?.value} value={t?.value}>{t?.label}</option>
                  ))}
                </select>
              </div>

              {/* Threshold */}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Umbral ({selectedUnit})</label>
                <input
                  type="number"
                  value={form?.threshold_value}
                  onChange={e => setForm(f => ({ ...f, threshold_value: e?.target?.value }))}
                  placeholder={`Ej: 30 ${selectedUnit}`}
                  className="w-full px-3 py-2 text-xs bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>

              {/* Metal scope */}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Metal (opcional)</label>
                <select
                  value={form?.scope_metal_id}
                  onChange={e => setForm(f => ({ ...f, scope_metal_id: e?.target?.value }))}
                  className="w-full px-3 py-2 text-xs bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  <option value="">Todos los metales</option>
                  {(metals || [])?.map(m => (
                    <option key={m?.id} value={m?.id}>{m?.name}</option>
                  ))}
                </select>
              </div>

              {/* Category scope */}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Categoría (opcional)</label>
                <select
                  value={form?.scope_category_id}
                  onChange={e => setForm(f => ({ ...f, scope_category_id: e?.target?.value }))}
                  className="w-full px-3 py-2 text-xs bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  <option value="">Todas las categorías</option>
                  {(categories || [])?.map(c => (
                    <option key={c?.id} value={c?.id}>{c?.name}</option>
                  ))}
                </select>
              </div>

              {/* Enabled */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setForm(f => ({ ...f, is_enabled: !f?.is_enabled }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    form?.is_enabled ? 'bg-emerald-500' : 'bg-muted'
                  }`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                    form?.is_enabled ? 'translate-x-4' : 'translate-x-1'
                  }`} />
                </button>
                <span className="text-xs text-foreground">Regla activa</span>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-xs font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !form?.threshold_value}
                className="px-4 py-2 text-xs font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ background: 'var(--color-accent)' }}
              >
                {submitting ? 'Guardando...' : editingRule ? 'Guardar cambios' : 'Crear regla'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-sm" style={{ boxShadow: 'var(--shadow-xl)' }}>
            <h3 className="text-sm font-semibold text-foreground mb-2">¿Eliminar regla?</h3>
            <p className="text-xs text-muted-foreground mb-5">Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-xs font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80 transition-colors">Cancelar</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
